/**
 * Special functions behind every probability the statistics tools print.
 *
 * Small, dependency-free implementations of the standard algorithms, chosen for
 * accuracy at the precision a results table shows (four to six significant digits) with
 * a wide margin to spare:
 *
 * - `logGamma`: Lanczos approximation (g = 7, n = 9), relative error below 1e-15 for
 *   positive arguments.
 * - `regularizedGammaP` / `Q`: the series for x < a + 1 and Lentz's continued fraction
 *   otherwise (Numerical Recipes §6.2).
 * - `regularizedBeta`: Lentz's continued fraction with the symmetry
 *   I_x(a, b) = 1 − I_{1−x}(b, a) for convergence (Numerical Recipes §6.4).
 *
 * The distribution functions in `distributions.ts` are thin wrappers over these, and
 * `stats.test.ts` pins them against values computed in R.
 */

const LANCZOS_G = 7;
const LANCZOS_COEFFICIENTS = [
  0.999_999_999_999_809_9, 676.520_368_121_885_1, -1259.139_216_722_402_8,
  771.323_428_777_653_1, -176.615_029_162_140_6, 12.507_343_278_686_905,
  -0.138_571_095_265_720_12, 9.984_369_578_019_572e-6,
  1.505_632_735_149_311_6e-7,
];

const MAX_ITERATIONS = 500;
const EPSILON = 3e-16;
const TINY = 1e-300;

/** ln Γ(x) for x > 0. */
export function logGamma(x: number): number {
  if (x < 0.5) {
    // Reflection: Γ(x)Γ(1 − x) = π / sin(πx).
    return (
      Math.log(Math.PI / Math.abs(Math.sin(Math.PI * x))) - logGamma(1 - x)
    );
  }
  const z = x - 1;
  let sum = LANCZOS_COEFFICIENTS[0];
  for (let i = 1; i < LANCZOS_G + 2; i++) {
    sum += LANCZOS_COEFFICIENTS[i] / (z + i);
  }
  const t = z + LANCZOS_G + 0.5;
  return (
    0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(sum)
  );
}

/** ln B(a, b). */
export function logBeta(a: number, b: number): number {
  return logGamma(a) + logGamma(b) - logGamma(a + b);
}

/** ln n! — exact through the gamma function, safe far beyond 170!. */
export function logFactorial(n: number): number {
  return logGamma(n + 1);
}

/** ln C(n, k). */
export function logChoose(n: number, k: number): number {
  if (k < 0 || k > n) {
    return Number.NEGATIVE_INFINITY;
  }
  return logFactorial(n) - logFactorial(k) - logFactorial(n - k);
}

function gammaSeries(a: number, x: number): number {
  let term = 1 / a;
  let sum = term;
  let denominator = a;
  for (let n = 0; n < MAX_ITERATIONS; n++) {
    denominator += 1;
    term *= x / denominator;
    sum += term;
    if (Math.abs(term) < Math.abs(sum) * EPSILON) {
      break;
    }
  }
  return sum * Math.exp(-x + a * Math.log(x) - logGamma(a));
}

function gammaContinuedFraction(a: number, x: number): number {
  let b = x + 1 - a;
  let c = 1 / TINY;
  let d = 1 / b;
  let h = d;
  for (let i = 1; i <= MAX_ITERATIONS; i++) {
    const an = -i * (i - a);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < TINY) {
      d = TINY;
    }
    c = b + an / c;
    if (Math.abs(c) < TINY) {
      c = TINY;
    }
    d = 1 / d;
    const delta = d * c;
    h *= delta;
    if (Math.abs(delta - 1) < EPSILON) {
      break;
    }
  }
  return Math.exp(-x + a * Math.log(x) - logGamma(a)) * h;
}

/** Lower regularized incomplete gamma P(a, x). */
export function regularizedGammaP(a: number, x: number): number {
  if (x <= 0) {
    return 0;
  }
  if (x < a + 1) {
    return gammaSeries(a, x);
  }
  return 1 - gammaContinuedFraction(a, x);
}

/** Upper regularized incomplete gamma Q(a, x) = 1 − P(a, x), without cancellation. */
export function regularizedGammaQ(a: number, x: number): number {
  if (x <= 0) {
    return 1;
  }
  if (x < a + 1) {
    return 1 - gammaSeries(a, x);
  }
  return gammaContinuedFraction(a, x);
}

function betaContinuedFraction(x: number, a: number, b: number): number {
  const qab = a + b;
  const qap = a + 1;
  const qam = a - 1;
  let c = 1;
  let d = 1 - (qab * x) / qap;
  if (Math.abs(d) < TINY) {
    d = TINY;
  }
  d = 1 / d;
  let h = d;
  for (let m = 1; m <= MAX_ITERATIONS; m++) {
    const m2 = 2 * m;
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < TINY) {
      d = TINY;
    }
    c = 1 + aa / c;
    if (Math.abs(c) < TINY) {
      c = TINY;
    }
    d = 1 / d;
    h *= d * c;
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < TINY) {
      d = TINY;
    }
    c = 1 + aa / c;
    if (Math.abs(c) < TINY) {
      c = TINY;
    }
    d = 1 / d;
    const delta = d * c;
    h *= delta;
    if (Math.abs(delta - 1) < EPSILON) {
      break;
    }
  }
  return h;
}

/** Regularized incomplete beta I_x(a, b). */
export function regularizedBeta(x: number, a: number, b: number): number {
  if (x <= 0) {
    return 0;
  }
  if (x >= 1) {
    return 1;
  }
  const front = Math.exp(a * Math.log(x) + b * Math.log(1 - x) - logBeta(a, b));
  if (x < (a + 1) / (a + b + 2)) {
    return (front * betaContinuedFraction(x, a, b)) / a;
  }
  return 1 - (front * betaContinuedFraction(1 - x, b, a)) / b;
}

/** The error function, through P(½, x²) so it shares the gamma code's accuracy. */
export function erf(x: number): number {
  if (x === 0) {
    return 0;
  }
  const value = regularizedGammaP(0.5, x * x);
  return x > 0 ? value : -value;
}

/** 1 − erf(x), computed from Q to keep precision far into the tail. */
export function erfc(x: number): number {
  if (x >= 0) {
    return regularizedGammaQ(0.5, x * x);
  }
  return 1 + regularizedGammaP(0.5, x * x);
}

/**
 * Invert a monotonically increasing function by bisection.
 *
 * Bisection rather than Newton because the CDFs it inverts are cheap, bounded and
 * monotone, and a method that cannot diverge is worth sixty extra evaluations: every
 * quantile in a critical-value table goes through here. `hi` is doubled until it
 * brackets the target, so callers only need a lower bound and a guess.
 */
export function invertMonotone(
  f: (x: number) => number,
  target: number,
  lo: number,
  hi: number,
  tolerance = 1e-12
): number {
  let low = lo;
  let high = hi;
  let guard = 0;
  while (f(high) < target && guard < 200) {
    low = high;
    high *= 2;
    guard += 1;
  }
  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2;
    if (f(mid) < target) {
      low = mid;
    } else {
      high = mid;
    }
    if (high - low <= tolerance * Math.max(1, Math.abs(mid))) {
      break;
    }
  }
  return (low + high) / 2;
}
